// Imports for external libraries go here.
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useScrollStore, useStickyState } from '@marriott/mi-store-utils';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { DefaultSecondaryNavigation } from '../../molecules/SecondaryNavigation/DefaultSecondaryNavigation';
import { HWSPrimaryDetachedNavigation } from '../../molecules/SecondaryNavigation/HWSPrimaryDetachedNavigation';
import { StyledSecondaryNavigationContainer } from './SecondaryNavigation.styles';
import { useSecondaryNavigationStore } from '../../store';
import { SecondaryNavigationProps } from './SecondaryNavigation.types';

export const SecondaryNavigationConfig = {
  emptyLabel: 'SecondaryNavigation',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/secondarynavigation`,
};

// Use named rather than default exports.
export const SecondaryNavigation: FC<SecondaryNavigationProps> = props => {
  const { model, activeMenuItem } = props;
  const setRegisteredComponent = useScrollStore(state => state.setRegisteredComponent);
  const navComponentOrder = useScrollStore(state => state.navComponentOrder);
  const scrollDirection = useScrollStore(state => state.scrollDirection);
  const prevScrollDirection = useScrollStore(state => state.prevScrollDirection);
  const { isSticky, top } = useStickyState(model?.componentId as string);
  const setSecondaryNavigationData = useSecondaryNavigationStore(state => state.setSecondaryNavigationData);
  const clearSecondaryNavigationData = useSecondaryNavigationStore(state => state.clearSecondaryNavigationData);
  const secondaryNavigationContainerRef = useRef<HTMLDivElement | null>(null);
  const stickyStartYRef = useRef(0);
  const componentId = model?.componentId;
  const [instantSticky, setInstantSticky] = useState(false);
  const isRegisteredInStore = useScrollStore(state =>
    componentId ? !!state.registeredComponents[componentId] : false
  );
  const shouldUseStoreSticky = !!componentId && navComponentOrder.includes(componentId) && isRegisteredInStore;
  const isStickyOnScrollUp = !!model?.scrollSetting?.isStickyOnScrollUp;
  const isStickyOnScrollDown = !!model?.scrollSetting?.isStickyOnScrollDown;
  const isScrollingUp = scrollDirection === 'up' || (scrollDirection === '' && prevScrollDirection === 'up');
  const isScrollingDown = scrollDirection === 'down' || (scrollDirection === '' && prevScrollDirection === 'down');
  const sticky = shouldUseStoreSticky ? isSticky || instantSticky : instantSticky;

  const fallbackTop = (() => {
    if (typeof document === 'undefined') {
      return 0;
    }
    const navEl = document.querySelector(
      '[data-component-name="o-common-static-navigationcontainer"], #navcontainer'
    ) as HTMLElement | null;
    const propertyBarEl = document.querySelector(
      '[data-component-name="o-common-static-propertybar"]'
    ) as HTMLElement | null;
    const isNavSticky =
      !!navEl && (navEl.classList.contains('header-sticky') || getComputedStyle(navEl).position === 'fixed');
    const isPropertyBarSticky = !!propertyBarEl && getComputedStyle(propertyBarEl).position === 'fixed';
    const navStickyHeight = isNavSticky && navEl ? navEl.getBoundingClientRect().height : 0;

    if (isPropertyBarSticky && propertyBarEl) {
      return Math.round(navStickyHeight + propertyBarEl.getBoundingClientRect().height);
    }
    if (isNavSticky && navEl) {
      return Math.round(navStickyHeight);
    }
    return 0;
  })();
  const resolvedTop = shouldUseStoreSticky ? Math.max(top, fallbackTop) : fallbackTop;

  const registerSecondaryNavigationComponent = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref || !componentId) {
        return;
      }

      setRegisteredComponent({
        componentId,
        isStickyOnScrollUp,
        isStickyOnScrollDown,
        height: ref.clientHeight,
      });
    },
    [componentId, isStickyOnScrollDown, isStickyOnScrollUp, setRegisteredComponent]
  );

  const secondaryNavigationRef = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref) {
        return;
      }
      secondaryNavigationContainerRef.current = ref;
      if (typeof window !== 'undefined') {
        stickyStartYRef.current = window.scrollY + ref.getBoundingClientRect().top;
      }
      registerSecondaryNavigationComponent(ref);
    },
    [registerSecondaryNavigationComponent]
  );

  useEffect(() => {
    if (!secondaryNavigationContainerRef.current || !componentId || navComponentOrder.length === 0) {
      return;
    }
    registerSecondaryNavigationComponent(secondaryNavigationContainerRef.current);
  }, [componentId, navComponentOrder.length, registerSecondaryNavigationComponent]);

  useEffect(() => {
    if (!secondaryNavigationContainerRef.current || !componentId || typeof ResizeObserver === 'undefined') {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (secondaryNavigationContainerRef.current) {
        registerSecondaryNavigationComponent(secondaryNavigationContainerRef.current);
      }
    });
    resizeObserver.observe(secondaryNavigationContainerRef.current);
    return () => resizeObserver.disconnect();
  }, [componentId, registerSecondaryNavigationComponent]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let previousY = window.scrollY || 0;
    const EDGE_TOLERANCE_PX = 2;
    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const isAtBottom = currentY >= maxScrollY - EDGE_TOLERANCE_PX;
      if (isAtBottom && currentY >= previousY) {
        previousY = currentY;
        return;
      }
      const stickyActivationY = Math.max(0, stickyStartYRef.current - fallbackTop);
      const hasStickyContextAbove = fallbackTop > 0;
      if (currentY <= 0) {
        setInstantSticky(false);
      } else if (currentY < previousY) {
        // On slight upward scroll, show immediately when a sticky context already exists above.
        setInstantSticky(isStickyOnScrollUp && (hasStickyContextAbove || currentY > stickyActivationY));
      } else if (currentY > previousY) {
        // Scrolling down should still respect the component's activation threshold.
        setInstantSticky(currentY > stickyActivationY && isStickyOnScrollDown);
      }
      previousY = currentY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [fallbackTop, isStickyOnScrollDown, isStickyOnScrollUp]);

  useEffect(() => {
    if (model) {
      setSecondaryNavigationData({
        model,
        activeMenuItem,
      });
    }

    return () => {
      clearSecondaryNavigationData();
    };
  }, [model, activeMenuItem, setSecondaryNavigationData, clearSecondaryNavigationData]);
  return (
    <StyledSecondaryNavigationContainer
      ref={secondaryNavigationRef}
      isSticky={sticky}
      top={resolvedTop}
      shouldAnimateTop={sticky && isScrollingUp}
      shouldAnimateBottom={sticky && isScrollingDown}
    >
      <EditableComponent config={SecondaryNavigationConfig} {...props}>
        {model?.variation === 'default navigation' ? (
          <DefaultSecondaryNavigation {...model} activeMenuItem={activeMenuItem} />
        ) : (
          <HWSPrimaryDetachedNavigation {...model} activeMenuItem={activeMenuItem} />
        )}
      </EditableComponent>
    </StyledSecondaryNavigationContainer>
  );
};
